import { useState, useEffect } from 'react'
import { useGameStore, Building } from '@store/gameStore'
import { GameLayout } from '@components/GameLayout'
import { AdModal } from '@components/AdModal'
import { getVIPIcon } from '@utils/vipHelpers'
import './CityScreen.css'

export function CityScreen() {
    const [selectedBuilding, setSelectedBuilding] = useState<Building | null>(null)
    const [now, setNow] = useState(Date.now())
    const [adForBuilding, setAdForBuilding] = useState<Building | null>(null)

    const buildings = useGameStore(state => state.buildings)
    const activeUpgrades = useGameStore(state => state.activeUpgrades) || {}
    const power = useGameStore(state => state.power)
    const spendGems = useGameStore(state => state.spendGems)
    const spendGold = useGameStore(state => state.spendGold)
    const trackWaitTime = useGameStore(state => state.trackWaitTime)
    const startUpgrade = useGameStore(state => state.startUpgrade)
    const finishUpgrade = useGameStore(state => state.finishUpgrade)
    const setScreen = useGameStore(state => state.setScreen)
    const lastDailyClaimGameDay = useGameStore(state => state.lastDailyClaimGameDay)
    const totalGameMinutes = useGameStore(state => state.totalGameMinutes)
    const empireColor = useGameStore(state => state.empireColor)
    const empireEmblem = useGameStore(state => state.empireEmblem)
    const username = useGameStore(state => state.username)
    const equipment = useGameStore(state => state.equipment) || {}
    const resources = useGameStore(state => state.resources)
    const isVIP = useGameStore(state => state.isVIP)
    const vipLevel = useGameStore(state => state.vipLevel)

    const currentDay = Math.floor(totalGameMinutes / (24 * 60))
    const canClaimDaily = lastDailyClaimGameDay !== currentDay

    // Calculate total power including equipment
    const equipmentPower = Object.values(equipment).reduce(
        (sum, item) => sum + (item?.power || 0),
        0
    )
    const totalPower = power + equipmentPower

    // UI Tick for countdowns
    useEffect(() => {
        const interval = setInterval(() => setNow(Date.now()), 1000)
        return () => clearInterval(interval)
    }, [])

    const handleStartUpgrade = (building: Building) => {
        startUpgrade(building.id)
        trackWaitTime(building.upgradeTimeSeconds * 1000)
        setSelectedBuilding(null)
    }

    const handleSkipWithGems = (building: Building) => {
        const isUpgrading = activeUpgrades[building.id] !== undefined
        // If instant upgrade (not started yet), pay gold first
        if (!isUpgrading) {
            if (resources.gold < building.upgradeCostGold) return // Should be blocked by UI, but double check
            if (!spendGold(building.upgradeCostGold)) return // Should not happen if check passed
        }

        if (spendGems(building.upgradeCostGems)) {
            finishUpgrade(building.id)
        }
        setSelectedBuilding(null)
    }

    const handleSkipWithAd = (building: Building) => {
        const isUpgrading = activeUpgrades[building.id] !== undefined
        // If instant upgrade (not started yet), check gold
        if (!isUpgrading) {
            if (resources.gold < building.upgradeCostGold) return
        }
        setAdForBuilding(building)
    }

    const handleAdComplete = () => {
        if (adForBuilding) {
            const isUpgrading = activeUpgrades[adForBuilding.id] !== undefined
            // If instant upgrade, pay gold now
            if (!isUpgrading) {
                if (!spendGold(adForBuilding.upgradeCostGold)) {
                    // Error if gold missing after watching ad? Should verify before showing ad.
                    // handleSkipWithAd checks, but race condition possible.
                    // If fails, we might just give upgrade anyway or fail.
                    // Let's assume fail but it's rare.
                    // Or better: just finish it as "sorry" reward if glitch?
                    // Strict:
                    setAdForBuilding(null)
                    return
                }
            }

            finishUpgrade(adForBuilding.id)
            setAdForBuilding(null)
            setSelectedBuilding(null)
        }
    }

    const formatTime = (seconds: number) => {
        if (seconds <= 0) return '0s'
        const mins = Math.floor(seconds / 60)
        const secs = Math.floor(seconds % 60)
        return mins > 0 ? `${mins}:${secs.toString().padStart(2, '0')}` : `${secs}s`
    }

    const getBuildingImage = (id: string, level: number) => {
        const typeMap: Record<string, string> = {
            'townhall': 'rathaus',
            'barracks': 'kaserne',
            'goldmine': 'goldmine',
            'warehouse': 'lager'
        }
        const typePrefix = typeMap[id] || id
        const clampedLevel = Math.min(Math.max(1, level), 5)
        return `/stadt/${typePrefix}${clampedLevel}.png`
    }

    return (
        <GameLayout noPadding noScroll showHeader={true} showNav={true}>
            <div className="city">
                {/* PLAYER INFO BOX (Top Right) */}
                <div className="city__player-info">
                    <div className="city__player-avatar">
                        <div className="city__emblem-shield" style={{ backgroundColor: empireColor }}>
                            {empireEmblem}
                        </div>
                    </div>
                    <div className="city__player-text">
                        <span className="city__player-name">{username} {getVIPIcon(vipLevel, isVIP)}</span>
                        <span className="city__player-power">{totalPower.toLocaleString()} ⚔️</span>
                    </div>
                </div>

                {/* SIDE NAVIGATION (Floating Icons) */}
                <div className="city__side-nav">
                    <div className={`city__side-icon ${canClaimDaily ? 'city__side-icon--active' : ''}`} onClick={() => setScreen('daily')}>
                        🎁
                        <span className="city__side-label">TAGESBONUS</span>
                    </div>
                    <div className="city__side-icon" onClick={() => setScreen('battlepass')}>
                        ⚔️
                        <span className="city__side-label">BATTLE PASS</span>
                    </div>
                    <div className="city__side-icon" onClick={() => setScreen('gacha')}>
                        ✨
                        <span className="city__side-label">LOOTBOX</span>
                    </div>
                </div>

                {/* BUILDING MAP (Absolute positions) */}
                <div className="city__map">
                    {buildings.map(building => {
                        const finishTime = activeUpgrades[building.id]
                        const isUpgrading = finishTime !== undefined
                        const timeRemaining = isUpgrading ? Math.max(0, (finishTime - now) / 1000) : 0

                        // Map internal ID to CSS class
                        const cssClassMap: Record<string, string> = {
                            'townhall': 'city__building--townhall',
                            'barracks': 'city__building--barracks',
                            'goldmine': 'city__building--goldmine',
                            'warehouse': 'city__building--warehouse'
                        }

                        return (
                            <div
                                key={building.id}
                                className={`city__building ${cssClassMap[building.id]} ${isUpgrading ? 'city__building--upgrading' : ''}`}
                                onClick={() => setSelectedBuilding(building)}
                            >
                                {isUpgrading && (
                                    <div className="city__building-progress-container" onClick={e => e.stopPropagation()}>
                                        <div className="city__building-progress-bar">
                                            <div
                                                className="city__building-progress-fill"
                                                style={{ width: `${Math.min(100, (1 - timeRemaining / building.upgradeTimeSeconds) * 100)}%` }}
                                            />
                                        </div>
                                        <div className="city__building-timer">
                                            ⏳ {formatTime(timeRemaining)}
                                        </div>
                                        <div className="city__building-quick-actions">
                                            <button className="city__quick-btn city__quick-btn--gems" onClick={() => handleSkipWithGems(building)}>
                                                💎 {building.upgradeCostGems}
                                            </button>
                                            <button className="city__quick-btn city__quick-btn--ad" onClick={() => handleSkipWithAd(building)}>
                                                📺 AD
                                            </button>
                                        </div>
                                    </div>
                                )}
                                <img
                                    src={getBuildingImage(building.id, building.level)}
                                    alt={building.name}
                                    className="city__building-img"
                                />
                                <div className="city__building-tag">
                                    <span>Lvl {building.level}</span>
                                    <span>{building.emoji}</span>
                                </div>
                            </div>
                        )
                    })}
                </div>

                {/* VIP Slot Lock Hint (Optional, could be positioned on map) */}
            </div>

            {/* REDESIGNED UPGRADE MODAL */}
            {selectedBuilding && (
                <div className="modal-overlay" onClick={() => setSelectedBuilding(null)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="city__modal-header">
                            <div className="city__modal-title-group">
                                <h2 className="city__modal-name">{selectedBuilding.name}</h2>
                                <span className="city__modal-subtitle">LEVEL {selectedBuilding.level} UPGRADE</span>
                            </div>
                            <button className="city__modal-close" onClick={() => setSelectedBuilding(null)}>✕</button>
                        </div>

                        <div className="modal__content">
                            <p className="city__modal-desc" style={{ marginBottom: '15px', fontSize: '14px' }}>
                                {selectedBuilding.description}
                            </p>

                            <div className="city__modal-info-grid">
                                <div className="city__modal-info-card">
                                    <span className="city__info-label">Upgrade Dauer</span>
                                    <span className="city__info-value">⏳ {formatTime(selectedBuilding.upgradeTimeSeconds)}</span>
                                </div>
                                <div className="city__modal-info-card">
                                    <span className="city__info-label">Energie Bonus</span>
                                    <span className="city__info-value">⚡ +{selectedBuilding.energyMax || 5}</span>
                                </div>
                                {selectedBuilding.powerBonus > 0 && (
                                    <div className="city__modal-info-card">
                                        <span className="city__info-label">Power Bonus</span>
                                        <span className="city__info-value">⚔️ +{selectedBuilding.powerBonus}</span>
                                    </div>
                                )}
                                {selectedBuilding.productionRate !== undefined && (
                                    <div className="city__modal-info-card">
                                        <span className="city__info-label">Produktion</span>
                                        <span className="city__info-value">💰 +{Math.round(selectedBuilding.productionRate * 0.3)}/m</span>
                                    </div>
                                )}
                            </div>

                            {(() => {
                                const townhall = buildings.find(b => b.id === 'townhall')
                                const isTownhall = selectedBuilding.id === 'townhall'
                                const meetsLevelRequirement = isTownhall || !townhall || selectedBuilding.level < townhall.level
                                const hasEnoughGold = resources.gold >= selectedBuilding.upgradeCostGold

                                // Logic matching gameStore: VIP 3 -> 4 slots, VIP 2 -> 3 slots, VIP 1 -> 2 slots, free -> 1 slot
                                let maxSlots = 1
                                if (isVIP) {
                                    if (vipLevel >= 3) maxSlots = 4
                                    else if (vipLevel >= 2) maxSlots = 3
                                    else maxSlots = 2
                                }
                                const activeUpgradeCount = Object.keys(activeUpgrades).length
                                const hasAvailableSlot = activeUpgradeCount < maxSlots

                                return (
                                    <>
                                        {!hasAvailableSlot && (
                                            <div className="city__requirement-warning" style={{ background: 'rgba(139, 92, 246, 0.15)', borderColor: 'rgba(139, 92, 246, 0.4)', color: '#c4b5fd' }}>
                                                ⏳ Alle Upgrade-Slots belegt! ({activeUpgradeCount}/{maxSlots})<br />
                                                {!isVIP && 'Als VIP kannst du mehr Gebäude gleichzeitig upgraden!'}
                                                {isVIP && vipLevel < 3 && 'Höhere VIP-Level schalten mehr Slots frei!'}
                                            </div>
                                        )}

                                        {!hasEnoughGold && (
                                            <div className="city__requirement-warning" style={{ background: 'rgba(245, 158, 11, 0.15)', borderColor: 'rgba(245, 158, 11, 0.4)', color: '#fbbf24' }}>
                                                💰 Nicht genug Gold! Du brauchst {selectedBuilding.upgradeCostGold} Gold.
                                            </div>
                                        )}

                                        {!meetsLevelRequirement && (
                                            <div className="city__requirement-warning">
                                                ⚠️ Rathaus Level {selectedBuilding.level + 1} benötigt!<br />
                                                Upgrade zuerst dein Rathaus.
                                            </div>
                                        )}

                                        <button
                                            className="btn--upgrade-gold"
                                            onClick={() => handleStartUpgrade(selectedBuilding)}
                                            disabled={!hasEnoughGold || !meetsLevelRequirement || !hasAvailableSlot}
                                        >
                                            💰 {selectedBuilding.upgradeCostGold} GOLD
                                            <span className="btn-upgrade-gold__sub">BAU STARTEN</span>
                                        </button>

                                        <div className="modal__divider">ODER</div>

                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button
                                                className="btn btn--primary"
                                                style={{ flex: 1, background: 'var(--color-gem)', border: 'none', fontWeight: 800, opacity: (!hasEnoughGold || !meetsLevelRequirement || resources.gems < selectedBuilding.upgradeCostGems) ? 0.5 : 1, cursor: (!hasEnoughGold || !meetsLevelRequirement || resources.gems < selectedBuilding.upgradeCostGems) ? 'not-allowed' : 'pointer' }}
                                                onClick={() => handleSkipWithGems(selectedBuilding)}
                                                disabled={!hasEnoughGold || !meetsLevelRequirement || resources.gems < selectedBuilding.upgradeCostGems}
                                            >
                                                💎 {selectedBuilding.upgradeCostGems} SOFORT
                                            </button>
                                            <button
                                                className="btn btn--ghost"
                                                style={{ flex: 1, border: '1px solid rgba(255,255,255,0.2)', fontWeight: 800, opacity: (!hasEnoughGold || !meetsLevelRequirement) ? 0.5 : 1, cursor: (!hasEnoughGold || !meetsLevelRequirement) ? 'not-allowed' : 'pointer' }}
                                                onClick={() => handleSkipWithAd(selectedBuilding)}
                                                disabled={!hasEnoughGold || !meetsLevelRequirement}
                                            >
                                                📺 ADS
                                            </button>
                                        </div>
                                    </>
                                )
                            })()}
                        </div>
                    </div>
                </div>
            )}

            {/* Ad Modal for skips (if triggered) */}
            {adForBuilding && (
                <AdModal
                    onComplete={handleAdComplete}
                    onCancel={() => setAdForBuilding(null)}
                />
            )}
        </GameLayout>
    )
}
